function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useState, useMemo } from 'react';
import MUIFormHelperText from '@mui/material/FormHelperText';
import MUIInputBase from '@mui/material/InputBase';
import MUIInputLabel from '@mui/material/InputLabel';
import MUIFormControl from '@mui/material/FormControl';
import MUIFormGroup from '@mui/material/FormGroup';
import MUIInputAdornment from '@mui/material/InputAdornment';
// eslint-disable-next-line import/no-extraneous-dependencies
import { unstable_useId as useId } from '@mui/utils';
import Select from "@mui/material/Select";
import { useTheme, styled, lighten } from '../styles';
import IconButton from "../IconButton";
// @todo: overridable component isn't working right in this context
// const FormHelperText = styled(MUIFormHelperText)(({ theme, error }: { theme: PixelTheme, error: boolean }) => ({
//   marginTop: theme.spacing(1),
//   color: error ? theme.palette.error.main : theme.palette.text.secondary,
//   ...theme.typography.caption,
// }));

export const InputBase = styled(MUIInputBase)(_ref => {
  let {
    theme,
    readOnly
  } = _ref;
  const bg = readOnly ? theme.palette.background.paper : lighten(theme.palette.secondary.main, 0.95);
  const bgHover = readOnly ? theme.palette.background.paper : theme.palette.background.default;
  const border = readOnly ? "1px solid ".concat(theme.palette.grey[300]) : "1px solid ".concat(theme.palette.grey[500]);
  const focusedBorderColor = readOnly ? theme.palette.grey[300] : theme.palette.secondary.main;
  return {
    flex: '1 0 10%',
    display: 'flex',
    borderRadius: theme.shape.borderRadius,
    border: border,
    background: bg,
    "&:hover": {
      background: bgHover
    },
    "&.Mui-disabled:hover": {
      background: bg
    },
    '&.Mui-focused': {
      background: theme.palette.background.paper,
      borderColor: focusedBorderColor
    },
    '&.Mui-error': {
      border: "1px solid ".concat(theme.palette.error.main)
    },
    '& .MuiInputBase-input': {
      padding: '.684em .5em',
      borderRadius: theme.shape.borderRadius,
      fontSize: theme.typography.fontSize,
      '&::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 1
      }
    },
    '&.MuiInputBase-adornedStart': {
      paddingLeft: '0.5em'
    },
    '&.MuiInputBase-adornedEnd': {
      paddingRight: '0.5em'
    },
    '&.MuiAutocomplete-inputRoot': {
      paddingRight: '0.5em !important'
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'static'
    },
    '& [type="search"]::-webkit-search-cancel-button': {
      WebkitAppearance: "none",
      appearance: "none"
    },
    '& [type="search"]::-webkit-search-decoration': {
      WebkitAppearance: "none",
      appearance: "none"
    }
  };
});
export const InputLabel = styled(MUIInputLabel)(_ref2 => {
  let {
    theme
  } = _ref2;
  return {
    position: "static",
    marginBottom: theme.spacing(1),
    transform: "unset",
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    "&.Mui-focused": {
      color: theme.palette.text.secondary
    },
    "&.Mui-disabled": {
      color: theme.palette.text.secondary
    },
    "&.Mui-error": {
      color: theme.palette.error.main
    }
  };
});
const IconButtonColored = styled(IconButton)(_ref3 => {
  let {
    theme
  } = _ref3;
  return {
    color: theme.palette.text.secondary
  };
});
const IconContainer = styled('div')(_ref4 => {
  let {
    theme
  } = _ref4;
  return {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    '& svg': {
      fontSize: '1.25em'
    }
  };
});
const Root = styled("div")(() => ({
  flex: 1,
  display: 'flex',
  minWidth: 100
}));
const InputContainer = styled('div')(_ref5 => {
  let {
    theme
  } = _ref5;
  return {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minHeight: 36
  };
});
const HelperContainer = styled('div')(_ref6 => {
  let {
    theme
  } = _ref6;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '&>div:first-child': {
      marginRight: theme.spacing(1)
    }
  };
});
export const PrefixContainer = styled('div')(_ref7 => {
  let {
    theme
  } = _ref7;
  return {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.fontSize
  };
});
const TextFieldForwardRef = /*#__PURE__*/React.forwardRef((_ref8, ref) => {
  let {
    augmentField,
    className,
    ...props
  } = _ref8;
  const theme = useTheme();
  const {
    children,
    showCount,
    showCountAriaLabel = 'Character Count',
    endingButtonProps,
    prefix,
    errorText,
    leadingIconProps,
    id: idOverride,
    ariaLabel,
    helperText,
    maxLength,
    inputProps = {},
    InputProps = {},
    inputRef,
    select = false,
    readOnly,
    SelectProps,
    value,
    isControlGrouped = false,
    focused,
    title,
    ...otherProps
  } = props;
  const id = useId(idOverride);
  const helperTextId = props.helperText && id ? "".concat(id, "-helper-text") : undefined;
  const inputLabelId = props.label && id ? "".concat(id, "-label") : undefined;
  let helperTextContent = props.error ? errorText : props.helperText;

  // add aria-label to input when there isn't a label otherwise
  if (!props.label && ariaLabel) {
    inputProps['aria-label'] = ariaLabel;
  }

  // derive char count from either the supplied value or the supplied defaultValue
  const val = props.value;
  const defaultVal = props.defaultValue;
  // eslint-disable-next-line no-nested-ternary
  const initialLength = val ? val.length : props.defaultValue ? defaultVal.length : 0;
  const [inputValueLength, setInputValueLength] = useState(initialLength);
  const handleInputChange = e => {
    setInputValueLength(e.target.value.length);
    if (props.onChange) {
      props.onChange(e);
    }
  };
  if (maxLength) {
    inputProps.maxLength = maxLength;
  }
  if (showCount) {
    const charCount = props.maxLength ? "".concat(inputValueLength, "/").concat(props.maxLength) : inputValueLength;
    helperTextContent = /*#__PURE__*/React.createElement(HelperContainer, {
      "aria-label": showCountAriaLabel
    }, /*#__PURE__*/React.createElement("div", null, helperTextContent), /*#__PURE__*/React.createElement("div", null, charCount));
  }
  let {
    startAdornment
  } = InputProps;
  if (props.prefix) {
    startAdornment = /*#__PURE__*/React.createElement(PrefixContainer, null, props.prefix);
  }
  if (props.leadingIconProps) {
    const LeadingIcon = props.leadingIconProps.icon;
    startAdornment = /*#__PURE__*/React.createElement(MUIInputAdornment, {
      position: "start"
    }, /*#__PURE__*/React.createElement(IconContainer, null, /*#__PURE__*/React.createElement(LeadingIcon, {
      "aria-label": props.leadingIconProps.ariaLabel
    })));
  }
  let {
    endAdornment
  } = InputProps;
  if (props.endingButtonProps) {
    const EndingButtonIcon = props.endingButtonProps.icon;
    const {
      disabled
    } = props.endingButtonProps;
    endAdornment = /*#__PURE__*/React.createElement(MUIInputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(IconButtonColored, {
      "aria-label": props.endingButtonProps.ariaLabel,
      onClick: e => props.endingButtonProps.onClick(e),
      disabled: disabled,
      size: "small",
      edge: "end"
    }, /*#__PURE__*/React.createElement(EndingButtonIcon, null)));
  }
  const {
    error,
    color,
    onFocus,
    onBlur,
    ...controlProps
  } = otherProps;
  const muiFormControlProps = isControlGrouped ? controlProps : controlProps;
  const muiInputBaseProps = otherProps;
  const MuiFormControlOrGroup = isControlGrouped ? MUIFormGroup : MUIFormControl;
  const InputElement = useMemo(() => !select && children ? /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flex: "1 0 auto"
    }
  }, children) : /*#__PURE__*/React.createElement(InputBase, _extends({
    title: title,
    id: id,
    ref: ref,
    readOnly: readOnly
  }, muiInputBaseProps, InputProps, {
    onChange: handleInputChange
    // onFocus={onFocus}
    // onBlur={onBlur}
    ,
    inputProps: inputProps,
    startAdornment: startAdornment,
    endAdornment: endAdornment,
    inputRef: inputRef,
    value: value
  })), [children, readOnly, id, inputProps, InputProps, handleInputChange, inputRef, startAdornment, endAdornment, value]);
  return /*#__PURE__*/React.createElement(Root, {
    "data-testid": "TextFieldRoot"
  }, /*#__PURE__*/React.createElement(MuiFormControlOrGroup, _extends({
    variant: "outlined",
    focused: focused
  }, muiFormControlProps), props.label && /*#__PURE__*/React.createElement(InputLabel, {
    shrink: true,
    htmlFor: id,
    id: inputLabelId
  }, props.label), /*#__PURE__*/React.createElement(InputContainer, {
    "data-testid": "InputContainer"
  }, select ? /*#__PURE__*/React.createElement(Select, _extends({
    title: title,
    variant: "filled",
    "aria-describedby": helperTextId,
    id: id,
    labelId: inputLabelId,
    value: value,
    input: InputElement,
    onFocus: onFocus,
    onBlur: onBlur,
    MenuProps: {
      elevation: 8
    }
  }, SelectProps), children) : InputElement, typeof augmentField === "function" && augmentField()), helperTextContent && /*#__PURE__*/React.createElement(MUIFormHelperText, {
    error: otherProps.error,
    component: "div",
    id: helperTextId,
    sx: {
      marginTop: theme.spacing(1),
      marginLeft: 0,
      marginRight: 0,
      color: otherProps.error ? theme.palette.error.main : theme.palette.text.secondary,
      ...theme.typography.caption
    }
  }, helperTextContent)));
});
export const TextField = props => /*#__PURE__*/React.createElement(TextFieldForwardRef, props);
export default TextField;